import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, X, Trash2, ArrowUpDown, CalendarClock, CircleDollarSign, ListChecks, Clock4, Users, CalendarDays, Music4, ShoppingBag, ShieldCheck, ShieldAlert } from "lucide-react";

const TABS = ["all", "pending", "confirmed", "rejected"];
const STATUS_ORDER = { pending: 0, confirmed: 1, rejected: 2 };

export default function AdminDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [beats, setBeats] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("all");
  const [section, setSection] = useState("bookings");
  const [sortBy, setSortBy] = useState("newest");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([api.get("/bookings"), api.get("/admin/stats"), api.get("/customers"), api.get("/beats")])
      .then(([b, s, c, bt]) => { setBookings(b.data); setStats(s.data); setCustomers(c.data); setBeats(bt.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); else setLoading(false); }, [user, load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  if (user && user.role !== "admin") {
    return (
      <div className="grain min-h-screen relative">
        <Navbar />
        <div className="px-6 py-32 text-center relative z-10">
          <h1 className="font-display text-3xl font-bold">Access denied</h1>
          <p className="text-[#A0A0A0] mt-3">This area is for studio administrators only.</p>
        </div>
      </div>
    );
  }

  const filtered = tab === "all" ? bookings : bookings.filter((b) => b.status === tab);
  const sorted = sortBy === "status"
    ? [...filtered].sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9))
    : filtered;
  const sectionTitle = { bookings: "Bookings", customers: "Customers", beats: "Beat Sales" }[section];
  const statCards = stats ? [
    { icon: ListChecks, label: "Total", value: stats.total },
    { icon: Clock4, label: "Pending", value: stats.pending },
    { icon: CalendarClock, label: "Confirmed", value: stats.confirmed },
    { icon: Users, label: "Customers", value: stats.customers },
    { icon: ShoppingBag, label: "Beats Sold", value: stats.beats_sold ?? 0 },
    { icon: CircleDollarSign, label: "Revenue", value: `RON ${stats.revenue}` },
  ] : [];

  return (
    <div className="grain min-h-screen relative">
      <Navbar />
      <section className="px-6 md:px-12 lg:px-20 pt-16 pb-8 relative z-10">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-3">Admin · Control Room</p>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{sectionTitle}</h1>
      </section>

      <section className="px-6 md:px-12 lg:px-20 relative z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((s) => (
          <div key={s.label} className="border border-white/10 bg-[#121212] p-6" data-testid={`stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
            <s.icon className="h-5 w-5 text-[#D90429] mb-3" strokeWidth={1.5} />
            <p className="font-display text-3xl font-bold">{s.value}</p>
            <p className="text-xs uppercase tracking-[0.2em] text-[#A0A0A0] mt-1">{s.label}</p>
          </div>
        ))}
      </section>

      <section className="px-6 md:px-12 lg:px-20 relative z-10 flex gap-3 mb-6 border-b border-white/10">
        {["bookings", "customers", "beats"].map((sec) => (
          <button key={sec} onClick={() => setSection(sec)} data-testid={`section-${sec}`}
            className={`px-1 pb-3 -mb-px text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${section === sec ? "border-[#D90429] text-white" : "border-transparent text-[#A0A0A0] hover:text-white"}`}>
            {sec}
          </button>
        ))}
      </section>

      {section === "bookings" && (
      <>
      <section className="px-6 md:px-12 lg:px-20 relative z-10 flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-all ${tab === t ? "bg-[#D90429] text-white" : "border border-white/10 text-[#A0A0A0] hover:border-white/30"}`}>
              {t}
            </button>
          ))}
        </div>
        <button onClick={() => setSortBy(sortBy === "status" ? "newest" : "status")} data-testid="sort-toggle"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider border border-white/10 text-[#A0A0A0] hover:border-[#D90429] hover:text-white transition-all">
          <ArrowUpDown className="h-4 w-4" /> Sort: {sortBy === "status" ? "Status" : "Newest"}
        </button>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24 relative z-10" data-testid="admin-bookings-table">
        {loading ? <p className="text-[#A0A0A0]">Loading...</p> : filtered.length === 0 ? (
          <p className="text-[#A0A0A0] border border-white/10 bg-[#121212] p-10 text-center">No bookings here.</p>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#121212] text-left text-xs uppercase tracking-wider text-[#A0A0A0]">
                  <th className="p-4">Client</th>
                  <th className="p-4">Room</th>
                  <th className="p-4">Date / Time</th>
                  <th className="p-4">Package</th>
                  <th className="p-4">Total</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b) => (
                  <tr key={b.booking_id} className="border-t border-white/10 hover:bg-white/[0.02]" data-testid={`admin-row-${b.booking_id}`}>
                    <td className="p-4">
                      <p className="font-medium text-white">{b.user_name}</p>
                      <p className="text-xs text-[#A0A0A0]">{b.user_email}</p>
                    </td>
                    <td className="p-4 text-white/80">{b.room_name}</td>
                    <td className="p-4 text-white/80">{b.date}<br/><span className="text-[#A0A0A0]">{b.start_time} · {b.hours}h</span></td>
                    <td className="p-4 text-white/80">{b.package_name}</td>
                    <td className="p-4 font-bold text-[#D90429]">RON {b.total_price}</td>
                    <td className="p-4">
                      <span className={`text-xs font-bold uppercase px-2 py-1 ${b.status === "confirmed" ? "bg-[#D90429] text-white" : b.status === "pending" ? "bg-white/10 text-white/70" : "text-[#A0A0A0] line-through"}`}>{b.status}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setStatus(b.booking_id, "confirmed")} disabled={b.status === "confirmed"}
                          data-testid={`confirm-${b.booking_id}`}
                          className="h-8 w-8 flex items-center justify-center border border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white disabled:opacity-30 transition-all">
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setStatus(b.booking_id, "rejected")} disabled={b.status === "rejected"}
                          data-testid={`reject-${b.booking_id}`}
                          className="h-8 w-8 flex items-center justify-center border border-white/20 text-white/60 hover:bg-white/10 disabled:opacity-30 transition-all">
                          <X className="h-4 w-4" />
                        </button>
                        {b.status === "rejected" && (
                          <button onClick={() => removeBooking(b.booking_id)} data-testid={`delete-booking-${b.booking_id}`}
                            title="Delete rejected booking"
                            className="h-8 w-8 flex items-center justify-center border border-white/15 text-white/50 hover:border-[#D90429] hover:text-[#D90429] transition-all">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </>
      )}

      {section === "customers" && (
      <section className="px-6 md:px-12 lg:px-20 pb-24 relative z-10" data-testid="admin-customers-table">
        {loading ? <p className="text-[#A0A0A0]">Loading...</p> : customers.length === 0 ? (
          <p className="text-[#A0A0A0] border border-white/10 bg-[#121212] p-10 text-center">No customers yet.</p>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#121212] text-left text-xs uppercase tracking-wider text-[#A0A0A0]">
                  <th className="p-4">Customer</th>
                  <th className="p-4">Email</th>
                  <th className="p-4">Role</th>
                  <th className="p-4">Bookings</th>
                  <th className="p-4">Beats</th>
                  <th className="p-4">Consent</th>
                  <th className="p-4">Joined</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.user_id} className="border-t border-white/10 hover:bg-white/[0.02]" data-testid={`customer-row-${c.user_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {c.picture ? <img src={c.picture} alt="" className="h-8 w-8 rounded-full border border-white/20" /> :
                          <div className="h-8 w-8 rounded-full bg-[#1A0306] border border-[#D90429]/40 flex items-center justify-center text-xs font-bold text-[#D90429]">{c.name?.[0]?.toUpperCase() || "?"}</div>}
                        <span className="font-medium text-white">{c.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-white/80">{c.email}</td>
                    <td className="p-4">
                      <span className={`text-xs font-bold uppercase px-2 py-1 ${c.role === "admin" ? "bg-[#D90429] text-white" : "bg-white/10 text-white/70"}`}>{c.role}</span>
                    </td>
                    <td className="p-4"><span className="inline-flex items-center gap-1 text-white/80"><CalendarDays className="h-4 w-4 text-[#D90429]" /> {c.booking_count}</span></td>
                    <td className="p-4"><span className="inline-flex items-center gap-1 text-white/80"><Music4 className="h-4 w-4 text-[#D90429]" /> {c.beats_owned}</span></td>
                    <td className="p-4" data-testid={`customer-consent-${c.user_id}`}>
                      {c.consent_given_at ? (
                        <span title={`First consent: ${c.consent_given_at}${c.consent_last_given_at ? `\nLast consent: ${c.consent_last_given_at}` : ""}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 bg-[#0E1F12] border border-emerald-500/40 text-emerald-400 cursor-help">
                          <ShieldCheck className="h-3.5 w-3.5" /> {c.consent_given_at.slice(0, 10)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 bg-white/5 border border-white/15 text-[#A0A0A0]">
                          <ShieldAlert className="h-3.5 w-3.5" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-[#A0A0A0]">{c.created_at ? c.created_at.slice(0, 10) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {section === "beats" && (
      <section className="px-6 md:px-12 lg:px-20 pb-24 relative z-10" data-testid="admin-beats-table">
        {loading ? <p className="text-[#A0A0A0]">Loading...</p> : beats.length === 0 ? (
          <p className="text-[#A0A0A0] border border-white/10 bg-[#121212] p-10 text-center">No beats uploaded yet.</p>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#121212] text-left text-xs uppercase tracking-wider text-[#A0A0A0]">
                  <th className="p-4">Beat</th>
                  <th className="p-4">Price</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Buyer</th>
                  <th className="p-4">Purchased</th>
                </tr>
              </thead>
              <tbody>
                {beats.map((bt) => (
                  <tr key={bt.beat_id} className="border-t border-white/10 hover:bg-white/[0.02]" data-testid={`admin-beat-row-${bt.beat_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-[#1A0306] border border-[#D90429]/40 flex items-center justify-center">
                          <Music4 className="h-4 w-4 text-[#D90429]" strokeWidth={1.5} />
                        </div>
                        <span className="font-medium text-white">{bt.title}</span>
                      </div>
                    </td>
                    <td className="p-4 font-bold text-[#D90429]">RON {bt.price}</td>
                    <td className="p-4">
                      <span className={`text-xs font-bold uppercase px-2 py-1 ${bt.status === "sold" ? "bg-[#D90429] text-white" : "bg-white/10 text-white/70"}`}>{bt.status}</span>
                    </td>
                    <td className="p-4 text-white/80">
                      {bt.status === "sold" ? (<><span className="text-white">{bt.buyer_name}</span><br/><span className="text-xs text-[#A0A0A0]">{bt.buyer_email}</span></>) : <span className="text-[#A0A0A0]">—</span>}
                    </td>
                    <td className="p-4 text-[#A0A0A0]">{bt.sold_at ? bt.sold_at.slice(0, 10) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}
    </div>
  );
}
